/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */

import { deleteData, fetchData, patchData, postData, putData } from '@jda/lui-portal-utilities';
import { getAuthToken } from '../auth/authProvider';
import { IRole, IAssignRoleToSubjectRequestBody } from '../models/Roles/RolesContracts';
import { IRoleRequest } from '../models/Roles/RolesContracts';
import { convertObjectToQueryString, createRequestObject, getAPIMUrl } from '../utility/commonUtility';

const { CLIENT_SERVICE_V1_URL } = window['env'];
export const getAllRoles = async (locationName?: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles`;
  return fetchData<IRole[]>(
    createRequestObject(apiURL),
    locationName
      ? {
        headers: {
          'LOCATION-CONTEXT': locationName
        }
      }
      : getAuthToken
  )
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const assignRoleToSubject = async (data: IAssignRoleToSubjectRequestBody, locationName?: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles/${data.roleId}/subjects`;
  return postData<any>(createRequestObject(apiURL), { id: data.subjectId },
    locationName ?
      {
        headers: {
          'LOCATION-CONTEXT': locationName
        }
      } : getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getSubjectIdFromSubjects = async (subjectId: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/users/${subjectId}`;

  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getLocationsToRole = async (roleId: string | number, queryParams: any): Promise<any> => {
  let queryData = '';
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles/${roleId}/locations`;

  if (queryParams && typeof queryParams === 'object') {
    queryData = convertObjectToQueryString(queryParams);
    apiURL = `${apiURL}?${queryData}`;
  }
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const associateDisassociateLocationToRole = async (id: number | string, data: any) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles/${id}/locations`;
  return patchData<any>(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const deleteRoleFromSubject = async (subjectId: string, roleId: number, locationName?: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles/${roleId}/subjects/${subjectId}`;
  return deleteData(createRequestObject(apiURL), {}, locationName ?
    {
      headers: {
        'LOCATION-CONTEXT': locationName
      }
    } : getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getRoleOfSubject = async (subjectId: string, locationName?: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/subjects/${subjectId}/roles`;
  return fetchData<IRole[]>(createRequestObject(apiURL),
    locationName ?
      {
        headers: {
          'LOCATION-CONTEXT': locationName
        }
      }
      : getAuthToken
  ).then(appsResponse => {
    return appsResponse;
  }).catch((error: any) => {
    throw error;
  });
};

export const createNewRole = async (data: IRoleRequest) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles`;

  return postData<any>(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const deleteRole = async (id: number | string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles/${id}`;
  return deleteData(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const updateRole = async (data: IRoleRequest, id: number) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles/${id}`;
  return putData(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};


export const exportRole = async (name: string, values: (string | number)[]) => {
  const data = {
    entityTypes: ['role', 'policy', 'permission'],
    filter: {
      entities: [
        {
          entityType: 'role',
          attributeName: name,
          attributeValues: values
        }
      ]
    },
    exportToALM: true
  }
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/data/export`;
  return postData(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};
